import type { SnEvent } from '../../features/event/types'
import { fetchAllEventPages } from './fetchAllEventPages'

/**
 * Query params for the membership events list.
 *
 * Deliberately the SAME shape `/games/follow` takes — My Schedule builds one
 * filter object and sends it to both, so games and events can never end up
 * bounded differently. Every field is optional: an absent key means "no
 * filter", which is not the same as sending an empty one.
 */
export interface ListMembershipEventsQuery {
  schedule_id?: string
  team_id?: string
  organization_id?: string
  venue_id?: string
  facility_id?: string
  /** Include descendants of the selected entity, as the games call does. */
  recursive?: number
  /**
   * NOT accepted here. `/games/follow` takes `membership_type`; this endpoint
   * does not, so the caller strips it — see `useMyScheduleEvents`. Left
   * documented rather than silently absent, since the two query shapes are
   * otherwise identical and the difference is easy to reintroduce by accident.
   */
  starts_after?: string
  starts_before?: string
  order?: string
  per_page?: number
  /** Mobile's Schedule tab filters by sport; web has no such control. */
  sport_id?: string
  generic_sport_id?: string
  /** Free-text search, mobile only. */
  query?: string
}

/**
 * Events the current user has a membership claim on, across every team they
 * follow — the event-side counterpart of `/games/follow`.
 *
 * Backs My Schedule, where membership scoping is exactly the point: that page
 * is by definition the viewer's own. A schedule's full event list — which
 * answers for everyone, not just the viewer — is `listScheduleEvents`.
 *
 * Replaces the previous approach for My Schedule, which had to resolve a
 * team's roster before it could ask for events and could therefore only answer
 * for ONE team at a time. This endpoint takes the same filters as the games
 * call — minus `membership_type`, which it does not accept — and answers for
 * all of them at once.
 *
 * Fully paginated — see `fetchAllEventPages` for why the list must be complete.
 *
 * Errors throw, like every other event service. An earlier version swallowed
 * 404/501 while the endpoint was still rolling out; now that it is deployed, a
 * missing endpoint is a real fault and should surface as one rather than
 * quietly reading as "no events".
 */
export const listMembershipEvents = async (
  query: ListMembershipEventsQuery = {}
): Promise<SnEvent[]> => fetchAllEventPages('/events/membership', query)
